import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { formatApiErrorDetail } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full px-4 py-3 rounded-xl bg-white border border-[#E0D5C7] text-[#2B231D] placeholder-[#A89C8F] focus:outline-none focus:border-[#9C6D3B] text-sm transition-colors";
const labelCls = "block text-xs font-semibold text-[#594E44] mb-1.5";
const btnCls =
  "inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#9C6D3B] text-white font-bold text-sm hover:bg-[#7F5830] transition-colors shadow-md disabled:opacity-60";

export const SettingsTab = () => {
  const [email, setEmail] = useState("");
  const [laden, setLaden] = useState(true);
  const [opslaan, setOpslaan] = useState(false);
  const [ww, setWw] = useState({ huidig: "", nieuw: "", bevestig: "" });
  const [wwLaden, setWwLaden] = useState(false);

  useEffect(() => {
    axios
      .get(`${API}/settings`, { withCredentials: true })
      .then((res) => setEmail(res.data.notification_email || ""))
      .catch(() => {})
      .finally(() => setLaden(false));
  }, []);

  const saveEmail = async (e) => {
    e.preventDefault();
    setOpslaan(true);
    try {
      await axios.put(`${API}/settings`, { notification_email: email }, { withCredentials: true });
      toast.success("Notificatie-adres opgeslagen");
    } catch (err) {
      toast.error("Opslaan mislukt", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setOpslaan(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (ww.nieuw !== ww.bevestig) {
      toast.error("Nieuwe wachtwoorden komen niet overeen");
      return;
    }
    setWwLaden(true);
    try {
      await axios.post(
        `${API}/auth/change-password`,
        { huidig_wachtwoord: ww.huidig, nieuw_wachtwoord: ww.nieuw },
        { withCredentials: true }
      );
      toast.success("Wachtwoord gewijzigd", { description: "Gebruik het nieuwe wachtwoord bij de volgende login." });
      setWw({ huidig: "", nieuw: "", bevestig: "" });
    } catch (err) {
      toast.error("Wijzigen mislukt", { description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setWwLaden(false);
    }
  };

  if (laden) return <p className="text-sm text-[#8C7A6B]">Instellingen laden...</p>;

  return (
    <div className="space-y-8 max-w-2xl" data-testid="settings-tab">
      <form onSubmit={saveEmail} className="bg-white rounded-3xl border border-[#EAE3DA] shadow-sm p-6 sm:p-8 space-y-5">
        <div>
          <h2 className="text-xl font-display font-bold text-[#2B231D]">E-mailnotificaties</h2>
          <p className="text-xs text-[#8C7A6B] mt-1">
            Op dit adres ontvangt René een e-mail zodra er een nieuwe aanvraag binnenkomt.
          </p>
        </div>
        <div>
          <label htmlFor="notif-email" className={labelCls}>Notificatie e-mailadres</label>
          <input
            id="notif-email"
            type="email"
            required
            data-testid="settings-email-input"
            className={inputCls}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="rene@example.nl"
          />
        </div>
        <button type="submit" data-testid="settings-email-save" disabled={opslaan} className={btnCls}>
          {opslaan && <Loader2 size={16} className="animate-spin" />}
          Adres opslaan
        </button>
      </form>

      <form onSubmit={changePassword} className="bg-white rounded-3xl border border-[#EAE3DA] shadow-sm p-6 sm:p-8 space-y-5">
        <div>
          <h2 className="text-xl font-display font-bold text-[#2B231D]">Wachtwoord wijzigen</h2>
          <p className="text-xs text-[#8C7A6B] mt-1">Kies een sterk wachtwoord van minimaal 8 tekens.</p>
        </div>
        <div>
          <label htmlFor="ww-huidig" className={labelCls}>Huidig wachtwoord</label>
          <input
            id="ww-huidig"
            type="password"
            required
            data-testid="settings-wachtwoord-huidig"
            className={inputCls}
            value={ww.huidig}
            onChange={(e) => setWw({ ...ww, huidig: e.target.value })}
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label htmlFor="ww-nieuw" className={labelCls}>Nieuw wachtwoord</label>
            <input
              id="ww-nieuw"
              type="password"
              required
              minLength={8}
              data-testid="settings-wachtwoord-nieuw"
              className={inputCls}
              value={ww.nieuw}
              onChange={(e) => setWw({ ...ww, nieuw: e.target.value })}
            />
          </div>
          <div>
            <label htmlFor="ww-bevestig" className={labelCls}>Bevestig nieuw wachtwoord</label>
            <input
              id="ww-bevestig"
              type="password"
              required
              minLength={8}
              data-testid="settings-wachtwoord-bevestig"
              className={inputCls}
              value={ww.bevestig}
              onChange={(e) => setWw({ ...ww, bevestig: e.target.value })}
            />
          </div>
        </div>
        <button type="submit" data-testid="settings-wachtwoord-save" disabled={wwLaden} className={btnCls}>
          {wwLaden && <Loader2 size={16} className="animate-spin" />}
          Wachtwoord wijzigen
        </button>
      </form>
    </div>
  );
};
